import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Box,
	Typography,
	IconButton,
	TextField,
	Button,
	Checkbox,
	keyframes,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DescriptionIcon from "@mui/icons-material/Description";
import LuggageIcon from "@mui/icons-material/Luggage";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BathtubIcon from "@mui/icons-material/Bathtub";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CelebrationIcon from "@mui/icons-material/Celebration";
import StarIcon from "@mui/icons-material/Star";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FlagIcon from "@mui/icons-material/Flag";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import PublicIcon from "@mui/icons-material/Public";
import { BasicModal } from "../BasicModal/BasicModal";
import { useWindowSize } from "@uidotdev/usehooks";

/* ───────── animaciones ───────── */
const popIn = keyframes`
  0%   { transform: scale(0.8); opacity: 0; }
  60%  { transform: scale(1.05); }
  100% { transform: scale(1);   opacity: 1; }
`;

const checkPop = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%      { transform: translateY(-4px); }
`;

const confettiFall = keyframes`
  0%   { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(60px) rotate(360deg); opacity: 0; }
`;

/* ───────── iconos por clave (sin emojis) ───────── */
const SECTION_ICONS = {
	description: DescriptionIcon,
	luggage: LuggageIcon,
	phone_iphone: PhoneIphoneIcon,
	bathtub: BathtubIcon,
	task_alt: TaskAltIcon,
	flight_takeoff: FlightTakeoffIcon,
	edit_note: EditNoteIcon,
	celebration: CelebrationIcon,
	star: StarIcon,
	favorite: FavoriteIcon,
	flag: FlagIcon,
	directions_walk: DirectionsWalkIcon,
	directions_run: DirectionsRunIcon,
	globe: PublicIcon,
};

const ICON_KEYS = Object.keys(SECTION_ICONS);

const EMOJI_TO_ICON = {
	"📄": "description",
	"🧳": "luggage",
	"📱": "phone_iphone",
	"🧴": "bathtub",
	"✅": "task_alt",
	"✈️": "flight_takeoff",
	"📝": "edit_note",
	"🎉": "celebration",
};

const getSectionIcon = (iconKey) => {
	let key = "edit_note";
	if (typeof iconKey === "string") {
		key = EMOJI_TO_ICON[iconKey] || (ICON_KEYS.includes(iconKey) ? iconKey : "edit_note");
	}
	const IconComponent = SECTION_ICONS[key] || SECTION_ICONS.edit_note;
	return IconComponent;
};

const getIconKey = (iconKey) => {
	if (typeof iconKey === "string" && ICON_KEYS.includes(iconKey)) return iconKey;
	if (typeof iconKey === "string" && EMOJI_TO_ICON[iconKey]) return EMOJI_TO_ICON[iconKey];
	return "edit_note";
};

/* ───────── color header scrapbooking ───────── */
const SCRAPBOOK_HEADER = "#C9B8A8";
const SCRAPBOOK_HEADER_DARK = "#B5A090";
const SCRAPBOOK_HEADER_TEXT = "#4A4035";

/* ───────── paleta pastel "cute" ───────── */
const PASTEL = {
	pink: "#FFB5C2",
	peach: "#FFDAB9",
	lavender: "#C3B1E1",
	mint: "#B5EAD7",
	sky: "#A0D2DB",
	lemon: "#FFF1A8",
	coral: "#FFB7A5",
	lilac: "#D4A5FF",
	sage: "#C1E1C1",
	rose: "#F4C2C2",
};

const CUTE_SECTIONS_DEFAULT = [
	{
		id: 1,
		title: "Documentos",
		icon: "description",
		color: PASTEL.coral,
		items: [
			{ id: 1, text: "Pasaporte en vigor", checked: false },
			{ id: 2, text: "Visado (si es necesario)", checked: false },
			{ id: 3, text: "Seguro de viaje", checked: false },
			{ id: 4, text: "Reservas de vuelos", checked: false },
			{ id: 5, text: "Reservas de hotel", checked: false },
		],
	},
	{
		id: 2,
		title: "Maleta",
		icon: "luggage",
		color: PASTEL.mint,
		items: [
			{ id: 1, text: "Ropa interior", checked: false },
			{ id: 2, text: "Camisetas / tops", checked: false },
			{ id: 3, text: "Pantalones / faldas", checked: false },
			{ id: 4, text: "Calzado cómodo", checked: false },
			{ id: 5, text: "Pijama", checked: false },
			{ id: 6, text: "Chaqueta / abrigo", checked: false },
		],
	},
	{
		id: 3,
		title: "Electrónica",
		icon: "phone_iphone",
		color: PASTEL.sky,
		items: [
			{ id: 1, text: "Cargador del móvil", checked: false },
			{ id: 2, text: "Power bank", checked: false },
			{ id: 3, text: "Adaptador de enchufe", checked: false },
			{ id: 4, text: "Auriculares", checked: false },
			{ id: 5, text: "Cámara de fotos", checked: false },
		],
	},
	{
		id: 4,
		title: "Aseo personal",
		icon: "bathtub",
		color: PASTEL.peach,
		items: [
			{ id: 1, text: "Cepillo y pasta de dientes", checked: false },
			{ id: 2, text: "Champú y gel", checked: false },
			{ id: 3, text: "Desodorante", checked: false },
			{ id: 4, text: "Protector solar", checked: false },
			{ id: 5, text: "Medicamentos", checked: false },
		],
	},
	{
		id: 5,
		title: "Antes del viaje",
		icon: "task_alt",
		color: PASTEL.lavender,
		items: [
			{ id: 1, text: "Avisar al banco", checked: false },
			{ id: 2, text: "Dejar llaves a alguien", checked: false },
			{ id: 3, text: "Revisar el tiempo", checked: false },
			{ id: 4, text: "Check-in online", checked: false },
			{ id: 5, text: "Descargar mapas offline", checked: false },
		],
	},
	{
		id: 6,
		title: "En el aeropuerto",
		icon: "flight_takeoff",
		color: PASTEL.lemon,
		items: [
			{ id: 1, text: "DNI / Pasaporte a mano", checked: false },
			{ id: 2, text: "Tarjeta de embarque", checked: false },
			{ id: 3, text: "Líquidos en bolsa (< 100 ml)", checked: false },
			{ id: 4, text: "Comprobar puerta de embarque", checked: false },
		],
	},
];

/* ───────── componente mini-confetti (iconos, sin emojis) ───────── */
const CONFETTI_COLORS = [
	"#fff",
	"#FFE082",
	"#FFB74D",
	"#F48FB1",
	"#CE93D8",
	"#90CAF9",
	"#A5D6A7",
];

const Confetti = ({ show }) => {
	if (!show) return null;
	const pieces = [
		{ Icon: StarIcon, color: CONFETTI_COLORS[0] },
		{ Icon: FavoriteIcon, color: CONFETTI_COLORS[1] },
		{ Icon: CheckCircleIcon, color: CONFETTI_COLORS[2] },
		{ Icon: StarIcon, color: CONFETTI_COLORS[3] },
		{ Icon: FavoriteIcon, color: CONFETTI_COLORS[4] },
		{ Icon: CheckCircleIcon, color: CONFETTI_COLORS[5] },
		{ Icon: StarIcon, color: CONFETTI_COLORS[6] },
		{ Icon: FavoriteIcon, color: CONFETTI_COLORS[0] },
	];
	return (
		<Box
			sx={{
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				pointerEvents: "none",
				overflow: "hidden",
				zIndex: 20,
			}}
		>
			{pieces.map(({ Icon, color }, i) => (
				<Box
					key={i}
					sx={{
						position: "absolute",
						top: "-10px",
						left: `${10 + i * 11}%`,
						color,
						animation: `${confettiFall} ${1.2 + i * 0.15}s ease-in forwards`,
						animationDelay: `${i * 0.08}s`,
					}}
				>
					<Icon sx={{ fontSize: 16 }} />
				</Box>
			))}
		</Box>
	);
};

/* ───────── Progreso circular SVG cute ───────── */
const CircleProgress = ({ progress, size = 64, strokeWidth = 5, lightBg = false }) => {
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (progress / 100) * circumference;

	const getProgressColor = (p) => {
		if (p < 30) return PASTEL.coral;
		if (p < 60) return PASTEL.lemon;
		if (p < 90) return PASTEL.mint;
		return "#8BC34A";
	};

	const getProgressIcon = (p) => {
		if (p === 0) return "flag";
		if (p < 25) return "directions_walk";
		if (p < 50) return "directions_run";
		if (p < 75) return "flight_takeoff";
		if (p < 100) return "globe";
		return "celebration";
	};

	const IconKey = getProgressIcon(progress);
	const ProgressIcon = SECTION_ICONS[IconKey] || CelebrationIcon;

	return (
		<Box sx={{ position: "relative", width: size, height: size }}>
			<svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
				{/* fondo */}
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke={lightBg ? "rgba(74,64,53,0.2)" : "rgba(255,255,255,0.35)"}
					strokeWidth={strokeWidth}
				/>
				{/* progreso */}
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke={getProgressColor(progress)}
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					strokeLinecap="round"
					style={{ transition: "stroke-dashoffset 0.35s ease" }}
				/>
			</svg>
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexDirection: "column",
					color: lightBg ? SCRAPBOOK_HEADER_TEXT : "#fff",
				}}
			>
				<ProgressIcon sx={{ fontSize: 20 }} />
			</Box>
		</Box>
	);
};

/* ═══════════ TEMPLATE100 ═══════════ */
const Template100 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	registerScrollContainer,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const setScrollContainerRef = (domNode) => {
		if (typeof registerScrollContainer === "function") {
			registerScrollContainer(domNode);
		}
	};

	const pageData = useMemo(() => page.data || {}, [page.data]);
	const [sections, setSections] = useState(() => {
		const d = page.data || {};
		if (
			d.sections &&
			Array.isArray(d.sections) &&
			d.sections.length > 0
		) {
			return d.sections;
		}
		return CUTE_SECTIONS_DEFAULT;
	});

	const [showConfetti, setShowConfetti] = useState(false);
	const [lastCheckedKey, setLastCheckedKey] = useState(null);

	/* ── persistir ── */
	const updatePageData = useCallback(
		(newSections) => {
			updatePage({
				...page,
				data: { ...pageData, sections: newSections },
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[page, pageData, updatePage],
	);

	/* ── toggle ── */
	const handleToggleItem = (sectionIndex, itemIndex) => {
		const newSections = sections.map((s, si) =>
			si !== sectionIndex
				? s
				: {
						...s,
						items: s.items.map((it, ii) =>
							ii !== itemIndex ? it : { ...it, checked: !it.checked },
						),
					},
		);
		const item = newSections[sectionIndex].items[itemIndex];
		if (item.checked) {
			setLastCheckedKey(`${sectionIndex}-${itemIndex}-${Date.now()}`);
		}
		setSections(newSections);
		updatePageData(newSections);
	};

	/* confetti al 100 % */
	const totalItems = sections.reduce((a, s) => a + s.items.length, 0);
	const checkedItems = sections.reduce(
		(a, s) => a + s.items.filter((i) => i.checked).length,
		0,
	);
	const progress =
		totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

	useEffect(() => {
		if (progress === 100 && totalItems > 0) {
			setShowConfetti(true);
			const t = setTimeout(() => setShowConfetti(false), 2500);
			return () => clearTimeout(t);
		}
	}, [progress, totalItems]);

	/* ── añadir / editar / borrar items ── */
	const handleAddItem = (sectionIndex) => {
		const newSections = sections.map((s, si) => {
			if (si !== sectionIndex) return s;
			const newId = Math.max(0, ...s.items.map((i) => i.id)) + 1;
			return {
				...s,
				items: [...s.items, { id: newId, text: "Nuevo item", checked: false }],
			};
		});
		setSections(newSections);
		updatePageData(newSections);
	};

	const handleDeleteItem = (sectionIndex, itemIndex) => {
		const newSections = sections.map((s, si) =>
			si !== sectionIndex
				? s
				: { ...s, items: s.items.filter((_, ii) => ii !== itemIndex) },
		);
		setSections(newSections);
		updatePageData(newSections);
	};

	/* ── modales ── */
	const [editSectionModal, setEditSectionModal] = useState(false);
	const [editItemModal, setEditItemModal] = useState(false);
	const [editingSection, setEditingSection] = useState(null);
	const [editingItem, setEditingItem] = useState(null);
	const [editingSectionIndex, setEditingSectionIndex] = useState(null);
	const [tempTitle, setTempTitle] = useState("");
	const [tempIcon, setTempIcon] = useState("");
	const [tempColor, setTempColor] = useState("");
	const [tempItemText, setTempItemText] = useState("");

	const handleEditItem = (sectionIndex, itemIndex) => {
		setEditingSectionIndex(sectionIndex);
		setEditingItem(itemIndex);
		setTempItemText(sections[sectionIndex].items[itemIndex].text);
		setEditItemModal(true);
	};
	const handleSaveItem = () => {
		const newSections = sections.map((s, si) =>
			si !== editingSectionIndex
				? s
				: {
						...s,
						items: s.items.map((it, ii) =>
							ii !== editingItem ? it : { ...it, text: tempItemText },
						),
					},
		);
		setSections(newSections);
		updatePageData(newSections);
		setEditItemModal(false);
	};

	const handleEditSection = (sectionIndex) => {
		setEditingSection(sectionIndex);
		setTempTitle(sections[sectionIndex].title);
		setTempIcon(getIconKey(sections[sectionIndex].icon));
		setTempColor(sections[sectionIndex].color);
		setEditSectionModal(true);
	};
	const handleSaveSection = () => {
		const newSections = sections.map((s, si) =>
			si !== editingSection
				? s
				: { ...s, title: tempTitle, icon: tempIcon, color: tempColor },
		);
		setSections(newSections);
		updatePageData(newSections);
		setEditSectionModal(false);
	};

	const handleAddSection = () => {
		const palette = Object.values(PASTEL);
		const newId = Math.max(0, ...sections.map((s) => s.id)) + 1;
		const newSections = [
			...sections,
			{
				id: newId,
				title: "Nueva sección",
				icon: "edit_note",
				color: palette[newId % palette.length],
				items: [{ id: 1, text: "Nuevo item", checked: false }],
			},
		];
		setSections(newSections);
		updatePageData(newSections);
	};

	const handleDeleteSection = (sectionIndex) => {
		const newSections = sections.filter((_, i) => i !== sectionIndex);
		setSections(newSections);
		updatePageData(newSections);
	};

	/* quitar emojis del título (pueden venir guardados en page.texts) */
	const stripEmojis = (str) => {
		if (typeof str !== "string") return "Mi Travel To-Do";
		return str
			.replace(
				/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}]|[\uFE00-\uFE0F]/gu,
				"",
			)
			.replace(/\s+/g, " ")
			.trim() || "Mi Travel To-Do";
	};

	const headerText = stripEmojis(page.texts?.[0]?.content);
	/* Template100 siempre usa estilo scrapbooking (beige papel) */
	const effectiveHeaderBg = undefined;
	const effectiveHeaderText = undefined;
	const useScrapbookHeader = true;

	const colorOptions = Object.values(PASTEL);

	const templateItemProps = {
		"data-template-item": true,
		className: "template-item",
	};

	/* helper: progreso por sección */
	const sectionProgress = (section) => {
		if (section.items.length === 0) return 0;
		return Math.round(
			(section.items.filter((i) => i.checked).length / section.items.length) *
				100,
		);
	};

	/* ── darken helper ── */
	const darken = (hex, amount = 40) => {
		let c = hex.replace("#", "");
		if (c.length === 3)
			c = c
				.split("")
				.map((ch) => ch + ch)
				.join("");
		const num = parseInt(c, 16);
		const r = Math.max(0, (num >> 16) - amount);
		const g = Math.max(0, ((num >> 8) & 0x00ff) - amount);
		const b = Math.max(0, (num & 0x0000ff) - amount);
		return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
	};

	const mainFont = fontFamily || "'Nunito', 'Poppins', sans-serif";

	return (
		<Box
			{...templateItemProps}
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#FFF8F0",
				fontFamily: `${mainFont} !important`,
				position: "relative",
				zIndex: 1,
				pointerEvents: "none",
				"& [data-template-item]": { pointerEvents: "auto" },
				"& [data-template100-scroll]": { pointerEvents: "auto" },
				"& *:not([data-template-header])": { fontFamily: `${mainFont} !important` },
			}}
		>
			{/* ═══════ HEADER ═══════ */}
			<Box
				data-block-id="header"
				sx={{
					background: `linear-gradient(180deg, ${effectiveHeaderBg || SCRAPBOOK_HEADER} 0%, ${effectiveHeaderBg ? darken(effectiveHeaderBg, 20) : SCRAPBOOK_HEADER_DARK} 100%)`,
					color: effectiveHeaderText ?? SCRAPBOOK_HEADER_TEXT,
					padding: isSmallScreen ? "22px 16px 18px" : "26px 22px 20px",
					position: "sticky",
					top: 0,
					zIndex: 10,
					borderRadius: "0 0 28px 28px",
					boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
					overflow: "hidden",
				}}
			>
				{/* icono decorativo maleta */}
				<Box
					sx={{
						position: "absolute",
						bottom: 12,
						left: 16,
						opacity: 0.07,
						pointerEvents: "none",
						color: "inherit",
					}}
				>
					<LuggageIcon sx={{ fontSize: 24 }} />
				</Box>

				{/* título + progreso */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
						position: "relative",
						zIndex: 2,
					}}
				>
					<CircleProgress
						progress={progress}
						size={isSmallScreen ? 54 : 62}
						strokeWidth={5}
						lightBg={useScrapbookHeader}
					/>
					<Box>
						<Typography
							sx={{
								fontSize: isSmallScreen
									? "17px !important"
									: "20px !important",
								fontWeight: "800 !important",
								lineHeight: "1.2 !important",
								letterSpacing: "-0.02em",
							}}
							dangerouslySetInnerHTML={{ __html: headerText }}
						/>
						<Typography
							sx={{
								fontSize: "12px !important",
								opacity: 0.92,
								mt: 0.3,
								fontWeight: "500 !important",
							}}
						>
							{progress === 100
								? "¡Todo listo para tu viaje!"
								: `${checkedItems} de ${totalItems} tareas completadas`}
						</Typography>
					</Box>
				</Box>

				{/* barra de progreso */}
				<Box
					sx={{
						mt: 1.5,
						mx: "auto",
						width: isSmallScreen ? "85%" : "65%",
						height: "6px",
						backgroundColor: useScrapbookHeader
							? "rgba(74,64,53,0.2)"
							: "rgba(255,255,255,0.2)",
						borderRadius: "8px",
						overflow: "hidden",
						position: "relative",
						zIndex: 2,
					}}
				>
					<Box
						sx={{
							width: `${progress}%`,
							height: "100%",
							borderRadius: "8px",
							backgroundColor: useScrapbookHeader
								? "rgba(74,64,53,0.5)"
								: "rgba(255,255,255,0.9)",
							transition: "width 0.35s ease",
						}}
					/>
				</Box>

				{/* confetti solo al 100% */}
				<Confetti show={showConfetti} />
			</Box>

			{/* ═══════ CONTENIDO ═══════ */}
			<Box
				ref={setScrollContainerRef}
				data-template100-scroll
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					WebkitOverflowScrolling: "touch",
				}}
			>
				<Box
					sx={{
						padding: isSmallScreen ? "16px 12px" : "22px 20px",
						display: "grid",
						gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(2, 1fr)",
						gap: isSmallScreen ? "14px" : "18px",
					}}
				>
					{sections.map((section, sectionIndex) => {
						const sp = sectionProgress(section);
						const sectionDone = sp === 100 && section.items.length > 0;

						return (
							<Box
								key={section.id}
								data-block-id={`text-${sectionIndex + 1}`}
								sx={{
									backgroundColor: "#fff",
									borderRadius: "20px",
									overflow: "hidden",
									boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
									border: `2px solid ${section.color}30`,
									transition: "all 0.3s ease",
									animation: `${popIn} 0.4s ease both`,
									animationDelay: `${sectionIndex * 0.06}s`,
									"&:hover": {
										transform: "translateY(-3px)",
										boxShadow: `0 8px 24px ${section.color}25`,
									},
									...(sectionDone && {
										border: `2px solid ${section.color}80`,
										background: `linear-gradient(135deg, #fff 0%, ${section.color}10 100%)`,
									}),
								}}
							>
								{/* ── header de sección ── */}
								<Box
									sx={{
										background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}CC 100%)`,
										padding: "12px 14px",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1,
										}}
									>
										{/* icono con badge */}
										<Box
											sx={{
												width: 34,
												height: 34,
												borderRadius: "10px",
												backgroundColor: "rgba(255,255,255,0.35)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: "18px",
												animation: sectionDone
													? `${float} 2s ease-in-out infinite`
													: "none",
											}}
										>
											{(() => {
											const IconC = sectionDone
												? CelebrationIcon
												: getSectionIcon(section.icon);
											return <IconC sx={{ fontSize: 18 }} />;
										})()}
										</Box>
										<Box>
											<Typography
												sx={{
													color: "#fff",
													fontWeight: "700 !important",
													fontSize: "13px !important",
													lineHeight: "1.2 !important",
													textShadow: "0 1px 2px rgba(0,0,0,0.1)",
												}}
											>
												{section.title}
											</Typography>
											<Typography
												sx={{
													color: "rgba(255,255,255,0.85)",
													fontSize: "10px !important",
													fontWeight: "500 !important",
												}}
											>
												{section.items.filter((i) => i.checked).length}/
												{section.items.length}{" "}
												{sectionDone ? "completado" : "tareas"}
											</Typography>
										</Box>
									</Box>
									<Box sx={{ display: "flex", gap: 0.25 }}>
										<Box
											{...templateItemProps}
											onClick={() => handleEditSection(sectionIndex)}
											sx={{
												display: "inline-flex",
												cursor: "pointer",
												alignItems: "center",
											}}
										>
											<IconButton
												size="small"
												sx={{
													color: "rgba(255,255,255,0.75)",
													pointerEvents: "none",
													transition: "all 0.2s",
													"&:hover": {
														color: "#fff",
														backgroundColor: "rgba(255,255,255,0.15)",
													},
												}}
											>
												<EditIcon sx={{ fontSize: 15 }} />
											</IconButton>
										</Box>
										<Box
											{...templateItemProps}
											onClick={() => handleDeleteSection(sectionIndex)}
											sx={{
												display: "inline-flex",
												cursor: "pointer",
												alignItems: "center",
											}}
										>
											<IconButton
												size="small"
												sx={{
													color: "rgba(255,255,255,0.75)",
													pointerEvents: "none",
													transition: "all 0.2s",
													"&:hover": {
														color: "#fff",
														backgroundColor: "rgba(255,255,255,0.15)",
													},
												}}
											>
												<DeleteIcon sx={{ fontSize: 15 }} />
											</IconButton>
										</Box>
									</Box>
								</Box>

								{/* ── mini progress bar ── */}
								<Box
									sx={{
										height: "3px",
										backgroundColor: `${section.color}20`,
									}}
								>
									<Box
										sx={{
											height: "100%",
											width: `${sp}%`,
											backgroundColor: section.color,
											transition: "width 0.4s ease",
											borderRadius: "0 2px 2px 0",
										}}
									/>
								</Box>

								{/* ── items ── */}
								<Box sx={{ padding: "6px 12px 10px" }}>
									{section.items.map((item, itemIndex) => {
										const checkedKey = `${sectionIndex}-${itemIndex}`;
										const justChecked =
											lastCheckedKey &&
											lastCheckedKey.startsWith(checkedKey + "-");

										return (
											<Box
												key={item.id}
												sx={{
													display: "flex",
													alignItems: "center",
													padding: "7px 4px",
													borderBottom:
														itemIndex < section.items.length - 1
															? "1px dashed #f0e8e0"
															: "none",
													gap: 0.5,
													borderRadius: "8px",
													transition: "background 0.2s",
													"&:hover": {
														backgroundColor: `${section.color}08`,
													},
												}}
											>
												<Box
													{...templateItemProps}
													onClick={() =>
														handleToggleItem(sectionIndex, itemIndex)
													}
													sx={{
														display: "inline-flex",
														cursor: "pointer",
														alignItems: "center",
													}}
												>
													<Checkbox
														checked={item.checked}
														icon={
															<RadioButtonUncheckedIcon
																sx={{
																	color: `${section.color}80`,
																	fontSize: 22,
																}}
															/>
														}
														checkedIcon={
															<CheckCircleIcon
																sx={{
																	color: section.color,
																	fontSize: 22,
																	animation:
																		justChecked && item.checked
																			? `${checkPop} 0.35s ease`
																			: "none",
																}}
															/>
														}
														sx={{
															padding: "3px",
															pointerEvents: "none",
														}}
													/>
												</Box>
												<Typography
													sx={{
														flex: 1,
														fontSize: "12.5px !important",
														fontWeight: item.checked
															? "400 !important"
															: "500 !important",
														color: item.checked ? "#bbb" : "#555",
														textDecoration: item.checked
															? "line-through"
															: "none",
														transition: "all 0.25s ease",
													}}
												>
													{item.text}
												</Typography>
												<Box
													{...templateItemProps}
													onClick={() =>
														handleEditItem(sectionIndex, itemIndex)
													}
													sx={{
														display: "inline-flex",
														cursor: "pointer",
														alignItems: "center",
														opacity: 0.35,
														transition: "opacity 0.2s",
														"&:hover": { opacity: 0.8 },
													}}
												>
													<IconButton
														size="small"
														sx={{ pointerEvents: "none" }}
													>
														<EditIcon sx={{ fontSize: 13 }} />
													</IconButton>
												</Box>
												<Box
													{...templateItemProps}
													onClick={() =>
														handleDeleteItem(sectionIndex, itemIndex)
													}
													sx={{
														display: "inline-flex",
														cursor: "pointer",
														alignItems: "center",
														opacity: 0.35,
														transition: "opacity 0.2s",
														"&:hover": { opacity: 0.8 },
													}}
												>
													<IconButton
														size="small"
														sx={{
															pointerEvents: "none",
															"&:hover": { color: "#ef4444" },
														}}
													>
														<DeleteIcon sx={{ fontSize: 13 }} />
													</IconButton>
												</Box>
											</Box>
										);
									})}

									{/* botón añadir item */}
									<Button
										{...templateItemProps}
										startIcon={
											<AddIcon sx={{ fontSize: "14px !important" }} />
										}
										onClick={() => handleAddItem(sectionIndex)}
										sx={{
											mt: 0.5,
											textTransform: "none",
											fontSize: "11px !important",
											fontWeight: "600 !important",
											color: section.color,
											borderRadius: "10px",
											padding: "4px 10px",
											transition: "all 0.2s",
											"&:hover": {
												backgroundColor: `${section.color}15`,
												transform: "scale(1.02)",
											},
										}}
									>
										Añadir tarea
									</Button>
								</Box>
							</Box>
						);
					})}

					{/* ── botón añadir sección ── */}
					<Box
						{...templateItemProps}
						onClick={handleAddSection}
						sx={{
							backgroundColor: "#fff",
							borderRadius: "20px",
							border: "2.5px dashed #e0d6cc",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							padding: "32px 20px",
							cursor: "pointer",
							transition: "all 0.3s ease",
							minHeight: 120,
							"&:hover": {
								borderColor: effectiveHeaderBg || SCRAPBOOK_HEADER_DARK,
								backgroundColor: `${effectiveHeaderBg || SCRAPBOOK_HEADER}25`,
								transform: "scale(1.02)",
								"& .add-icon-box": {
									transform: "rotate(90deg) scale(1.1)",
								},
							},
						}}
					>
						<Box
							className="add-icon-box"
							sx={{
								width: 44,
								height: 44,
								borderRadius: "14px",
								backgroundColor: `${effectiveHeaderBg || SCRAPBOOK_HEADER}25`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mb: 1,
								transition: "transform 0.3s ease",
							}}
						>
							<AddIcon
								sx={{
									fontSize: 24,
									color: effectiveHeaderBg || SCRAPBOOK_HEADER_DARK,
								}}
							/>
						</Box>
						<Typography
							sx={{
								color: "#b0a090",
								fontSize: "12px !important",
								fontWeight: "600 !important",
							}}
						>
							Añadir sección
						</Typography>
					</Box>
				</Box>

				{/* footer cute */}
				<Box
					sx={{
						textAlign: "center",
						pb: 3,
						pt: 1,
					}}
				>
					<Typography
						sx={{
							fontSize: "11px !important",
							color: "#c0b0a0",
							fontWeight: "500 !important",
						}}
					>
						{progress === 100
							? "¡Viaje preparado al 100%!"
							: progress >= 50
								? "¡Vas por buen camino!"
								: "¡Empieza a preparar tu viaje!"}
					</Typography>
				</Box>
			</Box>

			{/* ═══════ MODAL EDITAR SECCIÓN ═══════ */}
			<BasicModal
				width={420}
				showModal={editSectionModal}
				handleClose={() => setEditSectionModal(false)}
				contentSx={{ borderRadius: "20px !important" }}
			>
				<Box sx={{ padding: 2.5 }}>
					<Typography
						sx={{
							fontSize: "15px !important",
							fontWeight: "700 !important",
							mb: 2,
							color: "#444",
						}}
					>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", mr: 0.5, verticalAlign: "middle" }}>
							<EditIcon sx={{ fontSize: 18 }} />
						</Box>
						Editar sección
					</Typography>
					<TextField
						label="Título"
						value={tempTitle}
						onChange={(e) => setTempTitle(e.target.value)}
						fullWidth
						margin="normal"
						size="small"
						sx={{
							"& .MuiOutlinedInput-root": { borderRadius: "12px" },
						}}
					/>
					<Typography sx={{ fontSize: 12, color: "#888", mt: 2, mb: 1, fontWeight: 600 }}>
						Icono
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						{ICON_KEYS.filter((k) => !["flag", "directions_walk", "directions_run", "globe"].includes(k)).map((key) => {
							const IconC = SECTION_ICONS[key];
							return (
								<Box
									key={key}
									onClick={() => setTempIcon(key)}
									sx={{
										width: 38,
										height: 38,
										borderRadius: "10px",
										backgroundColor: tempIcon === key ? `${tempColor || PASTEL.lavender}40` : "#f5f5f5",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										cursor: "pointer",
										border: tempIcon === key ? `2px solid ${tempColor || PASTEL.lavender}` : "2px solid transparent",
										transition: "all 0.15s ease",
										color: tempIcon === key ? (tempColor || PASTEL.lavender) : "#888",
										"&:hover": { backgroundColor: "#eee", transform: "scale(1.05)" },
									}}
								>
									<IconC sx={{ fontSize: 20 }} />
								</Box>
							);
						})}
					</Box>
					<Typography
						sx={{
							fontSize: "12px !important",
							color: "#888",
							mt: 2,
							mb: 1,
							fontWeight: "600 !important",
						}}
					>
						Color
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						{colorOptions.map((color) => (
							<Box
								key={color}
								onClick={() => setTempColor(color)}
								sx={{
									width: 34,
									height: 34,
									borderRadius: "10px",
									backgroundColor: color,
									cursor: "pointer",
									border:
										tempColor === color
											? "3px solid #555"
											: "3px solid transparent",
									transition: "all 0.15s ease",
									"&:hover": { transform: "scale(1.15)" },
								}}
							/>
						))}
					</Box>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditSectionModal(false)}
							sx={{
								borderRadius: "10px",
								textTransform: "none",
								fontWeight: 600,
								color: "#888",
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveSection}
							sx={{
								borderRadius: "10px",
								textTransform: "none",
								fontWeight: 600,
								background: `linear-gradient(135deg, ${PASTEL.lavender}, ${PASTEL.lilac})`,
								boxShadow: "none",
								"&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
							}}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>

			{/* ═══════ MODAL EDITAR ITEM ═══════ */}
			<BasicModal
				width={420}
				showModal={editItemModal}
				handleClose={() => setEditItemModal(false)}
				contentSx={{ borderRadius: "20px !important" }}
			>
				<Box sx={{ padding: 2.5 }}>
					<Typography
						sx={{
							fontSize: "15px !important",
							fontWeight: "700 !important",
							mb: 2,
							color: "#444",
						}}
					>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", mr: 0.5, verticalAlign: "middle" }}>
							<EditNoteIcon sx={{ fontSize: 18 }} />
						</Box>
						Editar tarea
					</Typography>
					<TextField
						label="Texto"
						value={tempItemText}
						onChange={(e) => setTempItemText(e.target.value)}
						fullWidth
						margin="normal"
						size="small"
						multiline
						rows={2}
						sx={{
							"& .MuiOutlinedInput-root": { borderRadius: "12px" },
						}}
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditItemModal(false)}
							sx={{
								borderRadius: "10px",
								textTransform: "none",
								fontWeight: 600,
								color: "#888",
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveItem}
							sx={{
								borderRadius: "10px",
								textTransform: "none",
								fontWeight: 600,
								background: `linear-gradient(135deg, ${PASTEL.lavender}, ${PASTEL.lilac})`,
								boxShadow: "none",
								"&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
							}}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>
		</Box>
	);
};

Template100.thumbnail = "/images/templates/template-todo.png";

export default Template100;
